import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import quateIcon from '@assets/placementpage-images/quate-icon.svg';
import avatar from '@assets/placement.png';
import avatarTwo from '@assets/placementpage-images/avatar-two.png';
import useMediaQuery from '@utils/hooks/useMediaQuery';

const CircularTestimonialCarousel = ({ testimonials = [], hereStudentsData }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);
  const animationRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  // Default testimonials using the imported avatar images
  const allTestimonials =
    hereStudentsData?.length > 0
      ? hereStudentsData.map((item) => ({
          id: item.id,
          name: item.Name,
          image: item.SecondaryImage,
          testimonial: item.Review.trim(),
        }))
      : [
          {
            id: 1,
            name: 'Sarah Johnson',
            image: avatar,
            testimonial:
              'I understood how to aptly present myself in an interview & started getting more responses from the interviewing...',
          },
          {
            id: 2,
            name: 'Michael Chen',
            image: avatarTwo,
            testimonial:
              'The placement team guided me through the entire process. I improved my package by 60%!',
          },
          {
            id: 3,
            name: 'Priya Sharma',
            image: avatar,
            testimonial:
              'The technical interview training was exceptional. I got multiple offers after completion.',
          },
          {
            id: 4,
            name: 'James Wilson',
            image: avatarTwo,
            testimonial:
              'From zero calls to multiple interviews in just two weeks after completing the program.',
          },
          {
            id: 5,
            name: 'Emma Rodriguez',
            image: avatar,
            testimonial:
              'The resume building workshop completely transformed my application. Results came quickly!',
          },
          {
            id: 6,
            name: 'David Patel',
            image: avatarTwo,
            testimonial:
              'The networking opportunities provided were invaluable. I connected with industry leaders!',
          },
        ];

  // Function to handle animation frame for smooth progress
  const animateProgress = useCallback((timestamp) => {
    if (!animationRef.current) {
      animationRef.current = timestamp;
    }

    const elapsed = timestamp - animationRef.current;
    // 4000ms total duration for a full cycle
    const newProgress = Math.min(elapsed / 4000, 1);

    setProgress(newProgress);

    if (newProgress < 1) {
      requestAnimationFrame(animateProgress);
    } else {
      // Reset for next cycle
      animationRef.current = null;
      rotateToNext();
    }
  }, []);

  // Function to rotate to next testimonial
  const rotateToNext = () => {
    setIsTransitioning(true);

    setTimeout(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % allTestimonials.length);
      setProgress(0);

      setTimeout(() => {
        setIsTransitioning(false);
        requestAnimationFrame(animateProgress);
      }, 300);
    }, 300);
  };

  // Initialize animation
  useEffect(() => {
    requestAnimationFrame(animateProgress);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const getCirclePosition = (index, totalItems) => {
    const outerArcIndices = [0, 1, 2, 3]; // 4 on outer arc
    const innerArcIndices = [4, 5]; // 2 on inner arc

    const isOuterArc = outerArcIndices.includes(index);
    const arcIndex = isOuterArc ? outerArcIndices.indexOf(index) : innerArcIndices.indexOf(index);

    const itemsOnThisArc = isOuterArc ? outerArcIndices.length : innerArcIndices.length;

    // Angle range for top 70% of circle (~ -160° to -20°)
    const startAngle = -Math.PI * 0.9;
    const endAngle = -Math.PI * 0.1;
    const angleRange = endAngle - startAngle;

    const angle = startAngle + (angleRange / (itemsOnThisArc - 1)) * arcIndex;
    // Increased radius values for larger circular arrangement
    // Increased from 220/150
    // const mobileRadius = isOuterArc ? 150 : 100; // Adjusted for mobile
    const radius = isMobile ? (isOuterArc ? 200 : 140) : isOuterArc ? 260 : 190;

    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;

    return { x, y };
  };

  return (
    <div className="relative flex  w-full items-center justify-center">
      <div className="relative left-0 mt-[16.875rem] flex w-full max-w-6xl flex-col-reverse items-center justify-center gap-[1.25rem] lg:left-[10.25rem] md:mt-[0] lg:flex-row">
        <div className="relative h-[22rem] w-[22rem] md:h-[28rem] md:w-[28rem]">
          {' '}
          <div className="absolute inset-0">
            <div className="clip-circle-top-70 h-full w-full  rounded-full border border-dashed border-[#FFC1CF]"></div>
          </div>
          <div className="absolute inset-8">
            <div className="clip-circle-top-70 h-full w-full rounded-full border border-dashed border-[#FFC1CF]"></div>
          </div>
          <div className="absolute inset-16">
            <div className="clip-circle-top-70 h-full w-full rounded-full border border-dashed border-[#FFC1CF]"></div>
          </div>
          <style>{`
            .clip-circle-top-70 {
              clip-path: polygon(0 0%, 100% 0%, 100% 80%, 0% 80%);
            }
          `}</style>
          
            <div className="absolute inset-20 flex items-center justify-center rounded-full bg-[#FFC1CF]">
              <div
                className={`relative h-32 w-72  -translate-y-10 transition-opacity duration-500 md:h-72 md:w-72 ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}
                style={{ marginBottom: '98px' }}
              >
                <Image
                  src={allTestimonials[activeIndex]?.image}
                  alt={`Featured ${allTestimonials[activeIndex]?.name}`}
                  width={311}
                  height={400}
                  sizes="(max-width: 768px) 18rem, 18rem"
                  className="mb-12 rounded-full object-cover"
                  priority
                />
              </div>
            </div>
          
          {/* Circular images around the main circle - increased size */}
          {allTestimonials?.map((testimonial, index) => {
            const position = getCirclePosition(index, allTestimonials.length);
            const isActive = index === activeIndex;

            // Calculate when this item's progress should be visible
            const itemProgress =
              progress * allTestimonials.length -
              ((index - activeIndex + allTestimonials.length) % allTestimonials.length);
            const normalizedProgress = Math.max(0, Math.min(1, itemProgress));

            return (
              <div
                key={`${testimonial.id}-${index}`} // Combine id and index to ensure uniqueness
                className="absolute h-12 w-12 -translate-x-1/2 -translate-y-1/2 transform"
                style={{
                  left: `calc(50% + ${position.x}px)`,
                  top: `calc(50% + ${position.y}px)`,
                }}
              >
                <svg className="absolute inset-0 h-full w-full -rotate-90">
                  {isActive && (
                    <circle
                      cx="50%"
                      cy="50%"
                      r="24"
                      stroke="#F00037"
                      strokeWidth="2"
                      fill="transparent"
                      strokeDasharray={`${2 * Math.PI * 24}`}
                      strokeDashoffset={`${
                        2 *
                        Math.PI *
                        24 *
                        (1 - (index === activeIndex ? progress : normalizedProgress))
                      }`}
                      className="transition-all duration-300"
                    />
                  )}
                </svg>

               
                  <div
                    className={`absolute inset-1 cursor-pointer overflow-hidden rounded-full border-2 ${
                      isActive ? 'border-white bg-[#EAEBE6]' : 'border-white bg-[#EAEBE6]'
                    }`}
                    onClick={() => {
                      if (!isTransitioning) {
                        setActiveIndex(index);
                        setProgress(0);
                        animationRef.current = null;
                      }
                    }}
                  >
                    <Image
                      src={testimonial?.image}
                      alt={testimonial?.name}
                      layout="fill"
                      sizes="(max-width: 768px) 3rem, 3rem"
                      className={`object-cover ${isActive ? '' : 'grayscale'}`}
                    />
                  </div>
             
              </div>
            );
          })}
        </div>

        <div className="relative ml-16 mb-4 w-64  max-w-md justify-end text-left  font-medium italic leading-normal transition-opacity duration-500">
          <div className="absolute -top-4 -left-8 mb-4 md:-top-8">
            {' '}
            <Image
              src={quateIcon}
              width={48} /* Increased from 36 */
              height={48} /* Increased from 36 */
              alt="quote-icon"
              className="absolute -top-8 -left-8 mb-4"
            />
          </div>
          <p
            style={{ color: '#1F1F1F' }}
            className="h-[100px] w-[250px] text-center font-proxima-nova text-[13px] italic text-[#1F1F1F] line-clamp-3 md:h-auto md:w-auto md:text-left  md:text-[16px] md:font-medium md:text-[#1F1F1F] md:line-clamp-6"
          >
            {allTestimonials[activeIndex]?.testimonial}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CircularTestimonialCarousel;
